import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { cognitoClient } from "../aws/cognitoClient";
import "../styles/SignIn.css";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../context/AuthContext";
import { sendPortalEvent, storeRefreshToken } from "../helpers/apiHelper";
import SelectMfaMethod from "../components/SelectMfaMethod";

const OTP_LENGTH = 8;
const MFA_SESSION_TTL_MS = 14.5 * 60 * 1000;
const RESEND_COOLDOWN_MS = 90 * 1000;
const LOCKOUT_RETRY_MS = 30 * 1000;

const normalizeDigits = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\D/g, "");

const createLoginAttemptId = () => {
  if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `login-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const formatCountdown = (remainingMs) => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const SignIn = () => {
  const [username, setUsername] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [step, setStep] = useState("enterUsername");
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState(null);
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [challengeStartedAt, setChallengeStartedAt] = useState(null);
  const [resendAvailableAt, setResendAvailableAt] = useState(null);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState(null);
  const [maskedPhone, setMaskedPhone] = useState(null);
  const [now, setNow] = useState(Date.now());
  const actionInFlightRef = useRef(false);
  const otpInputRefs = useRef([]);
  const otpCodeRef = useRef("");
  const autoSubmitCodeRef = useRef("");
  const handleSubmitOtpRef = useRef(null);
  const loginAttemptIdRef = useRef("");
  const otpChallengeGenerationRef = useRef(0);

  const navigate = useNavigate();
  const { signIn } = useAuth();
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

  const isEmailOtp = challenge?.ChallengeName === "EMAIL_OTP";
  const isWhatsAppOtp = challenge?.ChallengeName === "WHATSAPP_OTP";

  useEffect(() => {
    if (
      step !== "enterOtp" ||
      (!challengeStartedAt && !resendAvailableAt && !lockoutUntil)
    ) {
      return undefined;
    }

    setNow(Date.now());
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [step, challengeStartedAt, resendAvailableAt, lockoutUntil]);

  const sessionRemainingMs = useMemo(() => {
    if (!challenge?.Session || !challengeStartedAt) return 0;
    return Math.max(0, MFA_SESSION_TTL_MS - (now - challengeStartedAt));
  }, [challenge?.Session, challengeStartedAt, now]);

  const resendRemainingMs = useMemo(() => {
    if (!resendAvailableAt) return 0;
    return Math.max(0, resendAvailableAt - now);
  }, [resendAvailableAt, now]);

  const lockoutRemainingMs = useMemo(() => {
    if (!lockoutUntil) return 0;
    return Math.max(0, lockoutUntil - now);
  }, [lockoutUntil, now]);

  const hasValidSession = sessionRemainingMs > 0;
  const isResendCoolingDown = resendRemainingMs > 0;
  const isLockedOut = lockoutRemainingMs > 0;
  const otpDigits = useMemo(
    () => Array.from({ length: OTP_LENGTH }, (_, index) => otpCode[index] || ""),
    [otpCode],
  );

  useEffect(() => {
    if (step !== "enterOtp") return;

    const firstEmptyIndex = otpDigits.findIndex((digit) => !digit);
    const focusIndex = firstEmptyIndex === -1 ? OTP_LENGTH - 1 : firstEmptyIndex;
    otpInputRefs.current[focusIndex]?.focus();
  }, [step, otpDigits]);

  const normalizeCpfUsername = (value) => normalizeDigits(value);
  const normalizeOtp = (value) => normalizeDigits(value).slice(0, OTP_LENGTH);

  const setOtpValue = (value) => {
    const normalizedValue = normalizeOtp(value);
    // Keep rapid native mobile input events from rebuilding the code from stale React state.
    otpCodeRef.current = normalizedValue;
    setOtpCode(normalizedValue);
  };

  const clearFeedback = () => {
    setError("");
    setErrorType(null);
    setInfoMessage("");
  };

  const showError = (message, type = "generic") => {
    setError(message);
    setErrorType(type);
  };

  const getSelectedMethodFromChallengeName = (challengeName) => {
    if (challengeName === "EMAIL_OTP") return "EMAIL";
    if (challengeName === "SMS_OTP") return "SMS";
    if (challengeName === "WHATSAPP_OTP") return "WHATSAPP";
    return null;
  };

  const isSessionExpiredError = (e) => {
    const name = (e?.name || e?.__type || "").toString().toLowerCase();
    const msg = (e?.message || "").toString().toLowerCase();
    const combined = `${name} ${msg}`;
    return (
      combined.includes("invalid session") ||
      combined.includes("session is invalid") ||
      combined.includes("session expired") ||
      combined.includes("expired session") ||
      combined.includes("invalidsession") ||
      (combined.includes("invalidparameter") && combined.includes("session")) ||
      (combined.includes("notauthorized") && combined.includes("session"))
    );
  };

  const isInvalidAuthStateError = (e) => {
    const combined = `${e?.name || ""} ${e?.message || ""}`.toLowerCase();
    return (
      combined.includes("auth state") ||
      combined.includes("invalid code or auth state")
    );
  };

  const isLockedOutError = (e) => {
    const combined = `${e?.name || ""} ${e?.message || ""}`.toLowerCase();
    return (
      combined.includes("temporarily locked") ||
      combined.includes("too many invalid credentials attempts") ||
      combined.includes("too many failed attempts") ||
      combined.includes("too many requests")
    );
  };

  const isDisabledUserError = (e) => {
    const combined = `${e?.name || ""} ${e?.message || ""}`.toLowerCase();
    return (
      combined.includes("disabled user") ||
      combined.includes("user is disabled")
    );
  };

  const isInvalidCodeError = (e) => {
    const combined = `${e?.name || ""} ${e?.message || ""}`.toLowerCase();
    return (
      combined.includes("code mismatch") ||
      combined.includes("invalid code") ||
      combined.includes("incorrect code") ||
      combined.includes("otp") ||
      combined.includes("verification code")
    );
  };

  const mapMethodToCognitoAnswer = (method) => {
    if (method === "EMAIL") return "EMAIL_OTP";
    if (method === "SMS") return "SMS_OTP";
    if (method === "WHATSAPP") return "WHATSAPP_OTP";
    return null;
  };

  const getOtpFailureMessage = (e) => {
    if (isDisabledUserError(e)) {
      return {
        message: "Não foi possível acessar sua conta. Fale com o suporte.",
        type: "disabled",
      };
    }

    if (isLockedOutError(e)) {
      return {
        message:
          "Muitas tentativas. Aguarde alguns segundos antes de tentar novamente.",
        type: "lockout",
      };
    }

    if (isSessionExpiredError(e)) {
      return {
        message: "O código expirou. Toque em 'Reenviar código' para receber um novo.",
        type: "expired",
      };
    }

    if (isInvalidAuthStateError(e)) {
      return {
        message:
          "Não foi possível validar este código. Toque em 'Reenviar código' para receber um novo.",
        type: "invalid_auth_state",
      };
    }

    if (isInvalidCodeError(e)) {
      return {
        message: "O código informado está incorreto. Verifique e digite novamente.",
        type: "invalid_code",
      };
    }

    return {
      message: "Não foi possível validar o código. Tente novamente.",
      type: "generic",
    };
  };

  const getOtpHeading = () => {
    if (selectedMethod === "EMAIL" || isEmailOtp) {
      return "Confira o código enviado para o seu e-mail";
    }

    if (selectedMethod === "WHATSAPP" || isWhatsAppOtp) {
      return "Confira o código enviado para o seu WhatsApp";
    }

    return "Confira o código enviado para o seu telefone";
  };

  const getOtpDestinationMessage = () => {
    if (selectedMethod === "EMAIL" || isEmailOtp) {
      return maskedEmail
        ? `Enviamos um código para ${maskedEmail}.`
        : "Enviamos um código para o seu e-mail.";
    }

    if (selectedMethod === "WHATSAPP" || isWhatsAppOtp) {
      return maskedPhone
        ? `Enviamos um código para o WhatsApp ${maskedPhone}.`
        : "Enviamos um código para o seu WhatsApp.";
    }

    return maskedPhone
      ? `Enviamos um código para o telefone ${maskedPhone}.`
      : "Enviamos um código para o seu telefone.";
  };

  const getOtpAnalyticsMetadata = (overrides = {}) => {
    const { challengeStartedAt: overrideStartedAt, ...metadata } = overrides;
    const startedAt = overrideStartedAt ?? challengeStartedAt;
    const challengeAgeSeconds = startedAt
      ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
      : null;

    return {
      ...(loginAttemptIdRef.current
        ? { loginAttemptId: loginAttemptIdRef.current }
        : {}),
      ...(otpChallengeGenerationRef.current > 0
        ? { challengeGeneration: otpChallengeGenerationRef.current }
        : {}),
      ...(challengeAgeSeconds != null ? { challengeAgeSeconds } : {}),
      ...metadata,
    };
  };

  const reportOtpEvent = (eventType, metadata = {}) => {
    Promise.resolve(sendPortalEvent(eventType, null, metadata)).catch(() => {});
  };

  const setOtpChallenge = (resp, options = {}) => {
    const startedAt = Date.now();
    const methodOverride =
      options.methodOverride || getSelectedMethodFromChallengeName(resp?.ChallengeName);
    const challengeGeneration =
      options.challengeGeneration ?? otpChallengeGenerationRef.current + 1;

    setChallenge(resp);
    setChallengeStartedAt(startedAt);
    setResendAvailableAt(startedAt + RESEND_COOLDOWN_MS);
    setLockoutUntil(null);
    setStep("enterOtp");
    setOtpValue("");
    autoSubmitCodeRef.current = "";
    setSelectedMethod(methodOverride || selectedMethod);
    setError("");
    setErrorType(null);
    setInfoMessage(options.infoMessage || "");
    otpChallengeGenerationRef.current = challengeGeneration;
    reportOtpEvent(
      options.deliveryTrigger === "resend" ? "OTP_RESENT" : "OTP_SENT",
      getOtpAnalyticsMetadata({
        challengeStartedAt: startedAt,
        challengeGeneration,
        method: methodOverride || selectedMethod || "UNKNOWN",
      }),
    );
  };

  const resetForm = () => {
    setChallenge(null);
    setChallengeStartedAt(null);
    setResendAvailableAt(null);
    setLockoutUntil(null);
    setOtpValue("");
    autoSubmitCodeRef.current = "";
    loginAttemptIdRef.current = "";
    otpChallengeGenerationRef.current = 0;
    setSelectedMethod(null);
    setMaskedEmail(null);
    setMaskedPhone(null);
    setStep("enterUsername");
    clearFeedback();
  };

  const reportLoginFailed = (cpf, errorLike, metadata = {}) => {
    Promise.resolve(
      sendPortalEvent("LOGIN_FAILED", null, {
        reason:
          errorLike?.message ||
          errorLike?.code ||
          errorLike?.name ||
          "unknown",
        username: cpf,
        ...(loginAttemptIdRef.current
          ? { loginAttemptId: loginAttemptIdRef.current }
          : {}),
        ...metadata,
      }),
    ).catch(() => {});
  };

  const focusOtpIndex = (index) => {
    otpInputRefs.current[index]?.focus();
  };

  const updateOtpCode = (nextCode) => {
    setOtpValue(nextCode);
  };

  const currentOtpDigits = () =>
    Array.from(
      { length: OTP_LENGTH },
      (_, index) => otpCodeRef.current[index] || "",
    );

  const queueAutoSubmit = (nextCode, shouldAutoSubmit = true) => {
    const normalizedCode = normalizeOtp(nextCode);
    autoSubmitCodeRef.current =
      shouldAutoSubmit && normalizedCode.length === OTP_LENGTH
        ? normalizedCode
        : "";
  };

  const fillOtpFromDigits = (
    digits,
    startIndex = 0,
    shouldAutoSubmit = false,
  ) => {
    const nextDigits = currentOtpDigits();
    digits
      .slice(0, OTP_LENGTH - startIndex)
      .forEach((digit, offset) => {
        nextDigits[startIndex + offset] = digit;
      });

    const nextCode = nextDigits.join("");
    updateOtpCode(nextCode);

    const nextFocusIndex = Math.min(
      startIndex + digits.length,
      OTP_LENGTH - 1,
    );
    focusOtpIndex(nextFocusIndex);
    queueAutoSubmit(nextCode, shouldAutoSubmit);
  };

  const handleOtpInputChange = (index, rawValue) => {
    const digits = normalizeOtp(rawValue);

    if (!digits) {
      const nextDigits = currentOtpDigits();
      nextDigits[index] = "";
      updateOtpCode(nextDigits.join(""));
      return;
    }

    if (digits.length > 1) {
      fillOtpFromDigits(digits.split(""), index, false);
      return;
    }

    const nextDigits = currentOtpDigits();
    nextDigits[index] = digits;
    const nextCode = nextDigits.join("");
    updateOtpCode(nextCode);

    if (index < OTP_LENGTH - 1) {
      focusOtpIndex(index + 1);
    }

    queueAutoSubmit(nextCode, true);
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();

      const nextDigits = currentOtpDigits();
      if (nextDigits[index]) {
        nextDigits[index] = "";
        updateOtpCode(nextDigits.join(""));
        return;
      }

      if (index > 0) {
        nextDigits[index - 1] = "";
        updateOtpCode(nextDigits.join(""));
        focusOtpIndex(index - 1);
      }

      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusOtpIndex(index - 1);
    }

    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      event.preventDefault();
      focusOtpIndex(index + 1);
    }
  };

  const handleOtpPaste = (index, event) => {
    const pastedDigits = normalizeOtp(
      event.clipboardData?.getData("text") || "",
    );
    if (!pastedDigits) return;

    event.preventDefault();
    fillOtpFromDigits(pastedDigits.split(""), index, false);
  };

  const handleStart = async () => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    clearFeedback();
    setLoading(true);
    setChallenge(null);
    setChallengeStartedAt(null);
    setResendAvailableAt(null);
    setLockoutUntil(null);

    const cpf = normalizeCpfUsername(username);

    if (!cpf) {
      showError("Informe seu CPF para continuar.");
      actionInFlightRef.current = false;
      setLoading(false);
      return;
    }

    loginAttemptIdRef.current = createLoginAttemptId();
    otpChallengeGenerationRef.current = 0;

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

      const response = await fetch(
        `${API_BASE_URL}/bhp-client-portal/auth/mfa-destinations`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cpf }),
        },
      );

      if (!response.ok) {
        throw new Error("mfa-destinations unavailable");
      }

      const data = await response.json();
      const email = data?.maskedEmail || null;
      const phone = data?.maskedPhone || null;

      if (!email && !phone) {
        throw new Error("missing masked destinations");
      }

      setMaskedEmail(email);
      setMaskedPhone(phone);
    } catch {
      showError("Não foi possível iniciar o login. Verifique o CPF informado.");
      actionInFlightRef.current = false;
      setLoading(false);
      return;
    }

    try {
      const resp = await cognitoClient.send(
        new InitiateAuthCommand({
          AuthFlow: "USER_AUTH",
          ClientId: clientId,
          AuthParameters: {
            USERNAME: cpf,
          },
          ClientMetadata: {
            portal: "BHP Client Portal",
          },
        }),
      );

      if (resp.AuthenticationResult) {
        await handleSuccessfulAuth(resp.AuthenticationResult, cpf);
        return;
      }

      if (resp.ChallengeName === "SELECT_CHALLENGE") {
        setChallenge(resp);
        setChallengeStartedAt(Date.now());
        setStep("selectMethod");
        return;
      }

      if (
        resp.ChallengeName === "SMS_OTP" ||
        resp.ChallengeName === "EMAIL_OTP" ||
        resp.ChallengeName === "WHATSAPP_OTP"
      ) {
        setOtpChallenge(resp, { deliveryTrigger: "initial" });
        return;
      }

      showError("Resposta inesperada. Tente novamente.");
    } catch (e) {
      reportLoginFailed(cpf, e, { stage: "initiate_auth" });
      showError(
        "Não foi possível iniciar o login. Verifique seu CPF e tente novamente.",
      );
    } finally {
      actionInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleSelectMethod = async (
    method,
    challengeOverride = challenge,
    options = {},
  ) => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    clearFeedback();
    setLoading(true);
    setSelectedMethod(method);

    const cpf = normalizeCpfUsername(username);
    const cognitoAnswer = mapMethodToCognitoAnswer(method);
    const activeChallenge = challengeOverride;
    const activeChallengeStartedAt =
      options.challengeStartedAtOverride ?? challengeStartedAt;

    if (
      !activeChallenge?.Session ||
      !activeChallengeStartedAt ||
      Date.now() - activeChallengeStartedAt > MFA_SESSION_TTL_MS
    ) {
      reportLoginFailed(
        cpf,
        { message: "Client-side SELECT_CHALLENGE session expired" },
        { stage: "select_challenge", method },
      );
      showError("Sua sessão expirou. Inicie o login novamente.", "expired");
      setChallenge(null);
      setChallengeStartedAt(null);
      setSelectedMethod(null);
      setStep("enterUsername");
      actionInFlightRef.current = false;
      setLoading(false);
      return;
    }

    try {
      const resp = await cognitoClient.send(
        new RespondToAuthChallengeCommand({
          ClientId: clientId,
          ChallengeName: "SELECT_CHALLENGE",
          Session: activeChallenge?.Session,
          ChallengeResponses: {
            USERNAME: cpf,
            ANSWER: cognitoAnswer,
          },
          ClientMetadata: {
            preferredChannel: method,
          },
        }),
      );

      if (resp.AuthenticationResult) {
        await handleSuccessfulAuth(resp.AuthenticationResult, cpf);
        return;
      }

      if (
        resp.ChallengeName === "SMS_OTP" ||
        resp.ChallengeName === "EMAIL_OTP" ||
        resp.ChallengeName === "WHATSAPP_OTP"
      ) {
        setOtpChallenge(resp, {
          methodOverride: method,
          infoMessage: options.preserveInfoMessage || "",
          deliveryTrigger: options.deliveryTrigger || "initial",
        });
        return;
      }

      showError("Resposta inesperada. Tente novamente.");
    } catch (e) {
      reportLoginFailed(cpf, e, { stage: "select_challenge", method });
      if (isSessionExpiredError(e)) {
        showError("Sua sessão expirou. Inicie o login novamente.", "expired");
        setChallenge(null);
        setChallengeStartedAt(null);
        setSelectedMethod(null);
        setStep("enterUsername");
      } else {
        showError("Não foi possível continuar. Tente novamente.");
      }
    } finally {
      actionInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (actionInFlightRef.current || isResendCoolingDown) return;
    actionInFlightRef.current = true;
    clearFeedback();
    setLoading(true);

    const cpf = normalizeCpfUsername(username);

    if (!cpf) {
      showError("Informe seu CPF para continuar.");
      setStep("enterUsername");
      actionInFlightRef.current = false;
      setLoading(false);
      return;
    }

    if (!selectedMethod) {
      showError("Selecione como deseja receber o código.");
      setStep("selectMethod");
      actionInFlightRef.current = false;
      setLoading(false);
      return;
    }

    reportOtpEvent(
      "OTP_RESEND_REQUESTED",
      getOtpAnalyticsMetadata({ method: selectedMethod }),
    );

    try {
      const resp = await cognitoClient.send(
        new InitiateAuthCommand({
          AuthFlow: "USER_AUTH",
          ClientId: clientId,
          AuthParameters: {
            USERNAME: cpf,
          },
          ClientMetadata: {
            portal: "BHP Client Portal",
            resend: "true",
          },
        }),
      );

      if (resp.AuthenticationResult) {
        await handleSuccessfulAuth(resp.AuthenticationResult, cpf);
        return;
      }

      if (resp.ChallengeName === "SELECT_CHALLENGE") {
        const nextChallengeStartedAt = Date.now();
        setChallenge(resp);
        setChallengeStartedAt(nextChallengeStartedAt);
        setStep("selectMethod");

        actionInFlightRef.current = false;
        await handleSelectMethod(selectedMethod, resp, {
          challengeStartedAtOverride: nextChallengeStartedAt,
          deliveryTrigger: "resend",
          preserveInfoMessage:
            "Enviamos um novo código. Use o código mais recente. Os anteriores não funcionam mais.",
        });
        return;
      }

      if (
        resp.ChallengeName === "SMS_OTP" ||
        resp.ChallengeName === "EMAIL_OTP" ||
        resp.ChallengeName === "WHATSAPP_OTP"
      ) {
        setOtpChallenge(resp, {
          methodOverride: selectedMethod,
          deliveryTrigger: "resend",
          infoMessage:
            "Enviamos um novo código. Use o código mais recente. Os anteriores não funcionam mais.",
        });
        return;
      }

      showError("Resposta inesperada. Tente novamente.");
    } catch (e) {
      reportLoginFailed(cpf, e, {
        stage: "resend_otp",
        method: selectedMethod,
      });
      showError("Não foi possível reenviar o código. Tente novamente.");
    } finally {
      actionInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleSubmitOtp = async (otpOverride) => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setError("");
    setErrorType(null);
    setLoading(true);

    const cpf = normalizeCpfUsername(username);
    const cleanOtp = normalizeOtp(otpOverride ?? otpCode);
    const method =
      selectedMethod ||
      (isEmailOtp ? "EMAIL" : isWhatsAppOtp ? "WHATSAPP" : "SMS");

    if (cleanOtp.length !== OTP_LENGTH) {
      showError("Digite os 8 números do código.", "invalid_code");
      actionInFlightRef.current = false;
      setLoading(false);
      return;
    }

    if (isLockedOut) {
      showError(
        "Muitas tentativas. Aguarde alguns segundos antes de tentar novamente.",
        "lockout",
      );
      actionInFlightRef.current = false;
      setLoading(false);
      return;
    }

    if (!challenge?.ChallengeName || !challenge?.Session || !hasValidSession) {
      reportLoginFailed(
        cpf,
        { message: "Client-side OTP session expired" },
        getOtpAnalyticsMetadata({ stage: "submit_otp", method }),
      );
      showError(
        "O código expirou. Toque em 'Reenviar código' para receber um novo.",
        "expired",
      );
      setChallengeStartedAt(null);
      setOtpValue("");
      actionInFlightRef.current = false;
      setLoading(false);
      return;
    }

    try {
      const submissionSource = otpOverride ? "auto" : "manual";
      reportOtpEvent(
        "OTP_SUBMITTED",
        getOtpAnalyticsMetadata({ method, submissionSource }),
      );
      const resp = await cognitoClient.send(
        new RespondToAuthChallengeCommand({
          ClientId: clientId,
          ChallengeName: challenge.ChallengeName,
          Session: challenge.Session,
          ChallengeResponses: {
            USERNAME: cpf,
            ...(isEmailOtp
              ? { EMAIL_OTP_CODE: cleanOtp }
              : isWhatsAppOtp
                ? { WHATSAPP_OTP_CODE: cleanOtp }
                : { SMS_OTP_CODE: cleanOtp }),
          },
          ClientMetadata: {
            preferredChannel: method,
          },
        }),
      );

      if (resp.AuthenticationResult) {
        reportOtpEvent(
          "OTP_VERIFIED",
          getOtpAnalyticsMetadata({ method, submissionSource }),
        );
        await handleSuccessfulAuth(resp.AuthenticationResult, cpf, {
          method,
          submissionSource,
          ...getOtpAnalyticsMetadata(),
        });
        return;
      }

      showError("Não foi possível validar o código. Tente novamente.");
    } catch (e) {
      reportLoginFailed(cpf, e, {
        ...getOtpAnalyticsMetadata({ submissionSource: otpOverride ? "auto" : "manual" }),
        stage: "submit_otp",
        method,
      });

      const failure = getOtpFailureMessage(e);

      if (failure.type === "expired") {
        setChallengeStartedAt(null);
        setOtpValue("");
      }

      if (failure.type === "invalid_auth_state") {
        setOtpValue("");
      }

      if (failure.type === "lockout") {
        setLockoutUntil(Date.now() + LOCKOUT_RETRY_MS);
      }

      showError(failure.message, failure.type);
    } finally {
      actionInFlightRef.current = false;
      setLoading(false);
    }
  };

  handleSubmitOtpRef.current = handleSubmitOtp;

  const handleSuccessfulAuth = async (authTokens, cpf, loginMetadata = {}) => {
    try {
      const decoded = jwtDecode(authTokens.IdToken);
      const portalUserId = decoded["custom:portalUserId"];
      const legacyPartyId = decoded["custom:partyId"];
      const authFlow =
        portalUserId ? "portal_user" : legacyPartyId ? "legacy" : null;

      const userData = {
        username: cpf,
        email: decoded?.email,
        partyId: legacyPartyId,
        phone_number: decoded["phone_number"],
      };

      if (authTokens?.IdToken) {
        localStorage.setItem("idToken", authTokens.IdToken);
      }
      if (authTokens?.AccessToken) {
        localStorage.setItem("accessToken", authTokens.AccessToken);
      }
      if (portalUserId) {
        localStorage.setItem("portalUserId", portalUserId);
        localStorage.setItem("authFlow", "portal_user");
      } else if (legacyPartyId) {
        localStorage.setItem("clientId", legacyPartyId);
        localStorage.setItem("authFlow", "legacy");
      }
      if (authTokens?.RefreshToken) {
        localStorage.setItem("refreshToken", authTokens.RefreshToken);
      }

      if (authTokens.RefreshToken) {
        await storeRefreshToken(authTokens.RefreshToken);
      }

      localStorage.setItem("wasLoggedIn", "true");
      signIn(userData, authTokens);
      Promise.resolve(
        sendPortalEvent(
          "LOGIN_SUCCESS",
          legacyPartyId || null,
          {
            ...(authFlow ? { authFlow } : {}),
            ...loginMetadata,
          },
          authTokens.IdToken,
        ),
      ).catch(() => {});

      navigate("/", { replace: true });
      resetForm();
    } catch {
      showError("Não foi possível finalizar o login. Tente novamente.");
    }
  };

  useEffect(() => {
    if (
      step !== "enterOtp" ||
      !autoSubmitCodeRef.current ||
      otpCode.length !== OTP_LENGTH ||
      autoSubmitCodeRef.current !== otpCode ||
      loading ||
      !hasValidSession ||
      actionInFlightRef.current
    ) {
      return undefined;
    }

    const codeToSubmit = autoSubmitCodeRef.current;
    autoSubmitCodeRef.current = "";

    const timeoutId = window.setTimeout(() => {
      handleSubmitOtpRef.current?.(codeToSubmit);
    }, 50);

    return () => window.clearTimeout(timeoutId);
  }, [step, otpCode, loading, hasValidSession]);

  const otpStatusMessage = hasValidSession
    ? `Seu código expira em ${formatCountdown(sessionRemainingMs)}.`
    : "O código expirou. Toque em 'Reenviar código' para receber um novo.";

  const resendButtonLabel = isResendCoolingDown
    ? `Reenviar código em ${formatCountdown(resendRemainingMs)}`
    : "Reenviar código";

  const lockoutMessage =
    errorType === "lockout" && isLockedOut
      ? `Muitas tentativas. Aguarde ${formatCountdown(lockoutRemainingMs)} antes de tentar novamente.`
      : error;

  if (step === "selectMethod") {
    return (
      <SelectMfaMethod
        email={maskedEmail}
        phone={maskedPhone}
        loading={loading}
        onSelect={(method) => handleSelectMethod(method)}
      />
    );
  }

  if (step === "enterOtp") {
    return (
      <div className="signin-container">
        <div className="signin-card signin-card--otp">
          <h2>{getOtpHeading()}</h2>

          <p className="signin-supporting-text">{getOtpDestinationMessage()}</p>

          <p
            className={`signin-status ${hasValidSession ? "" : "signin-status--warning"}`.trim()}
            aria-live="polite"
          >
            {otpStatusMessage}
          </p>

          {infoMessage ? (
            <p className="signin-status signin-status--success" aria-live="polite">
              {infoMessage}
            </p>
          ) : null}

          {error ? (
            <p className="error" role="alert" aria-live="assertive">
              {lockoutMessage}
            </p>
          ) : null}

          <div className="signin-field">
            <label className="signin-label" htmlFor="otp-digit-0">
              Digite o código de 8 números
            </label>

            <div className="otp-inputs" aria-label="Código de verificação">
              {otpDigits.map((digit, index) => (
                <div className="otp-cell" key={`otp-digit-${index}`}>
                  <input
                    id={`otp-digit-${index}`}
                    ref={(element) => {
                      otpInputRefs.current[index] = element;
                    }}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    className="otp-input"
                    maxLength={1}
                    value={digit}
                    aria-label={`Dígito ${index + 1} do código`}
                    onInput={(event) =>
                      handleOtpInputChange(index, event.currentTarget.value)
                    }
                    onKeyDown={(event) => handleOtpKeyDown(index, event)}
                    onPaste={(event) => handleOtpPaste(index, event)}
                  />
                  <span className="otp-digit-display" aria-hidden="true">
                    {digit}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleSubmitOtp()}
            disabled={
              loading ||
              isLockedOut ||
              !hasValidSession ||
              normalizeOtp(otpCode).length !== OTP_LENGTH
            }
          >
            {loading ? "Validando..." : "Confirmar"}
          </button>

          <p className="signin-helper-text">
            Não recebeu? Verifique a caixa de spam / lixo eletrônico ou aguarde
            alguns segundos e reenvie.
          </p>

          <button
            type="button"
            onClick={handleResendOtp}
            disabled={loading || isResendCoolingDown}
            className="link-button"
          >
            {resendButtonLabel}
          </button>

          <button
            type="button"
            onClick={() => navigate("/support")}
            disabled={loading}
            className="link-button"
          >
            Precisa de ajuda para entrar?
          </button>

          <button
            type="button"
            onClick={resetForm}
            disabled={loading}
            className="link-button"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="signin-container">
      <div className="signin-card">
        <div className="signin-logo">
          <img
            src="/images/pgmbm-logo-horizontal-white.png"
            srcSet="/images/pgmbm-logo-horizontal-white.png 1x, /images/pgmbm-logo-horizontal-white@2x.png 2x"
            width="229"
            height="120"
            alt="PGMBM"
            className="signin-logo-img"
          />
        </div>
        <h1 className="signin-title">
          Bem-vindo ao Portal do Cliente do Caso Inglês Mariana
        </h1>

        <p className="signin-description">
          Faça login para acessar seu espaço protegido para clientes do Caso
          Inglês Mariana. Aqui, você pode encontrar informações sobre sua ação,
          ver documentos importantes, ver atualizações e notícias da ação, e
          muito mais.
        </p>

        {error ? (
          <p className="error" role="alert" aria-live="assertive">
            {error}
          </p>
        ) : null}

        <div className="signin-field">
          <label className="signin-label" htmlFor="cpf-username">
            CPF
          </label>

          <input
            id="cpf-username"
            type="text"
            placeholder="Digite seu CPF (apenas números)"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            inputMode="numeric"
            autoComplete="username"
            aria-describedby="cpf-help"
          />
        </div>

        <p id="cpf-help" className="signin-helper-text">
          Use apenas os números do CPF.
        </p>

        <button className="loginBtn" onClick={handleStart} disabled={loading}>
          {loading ? "Carregando..." : "Continuar"}
        </button>

        <button
          type="button"
          className="link-button"
          onClick={() => navigate("/support")}
          disabled={loading}
        >
          Precisa de ajuda para entrar?
        </button>

        <div className="business-button-wrapper">
          <button
            type="button"
            className="business-button"
            onClick={() => navigate("/cnpj")}
            disabled={loading}
          >
            Eu tenho um CNPJ
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
